/* ProductionCaseDetail — the production tech's one-stop case workspace.
 *
 * Before this page existed (built 2026-02-27) production techs only saw a
 * single row in the queue with 3 buttons. They couldn't open the case to
 * read the planner's plan, download STL files, or see what materials they
 * had consumed. They were also blind to refinement/retainer linkage and
 * had no way to print a packing slip without going through the queue's
 * tiny inline link.
 *
 * This detail page consolidates everything the tech needs while working
 * a case — without exposing PHI from the prescription (production tech
 * is blocked on that PDF and we don't re-render prescription fields
 * here).
 *
 * Layout
 *   [Back to queue] [Download PDF dropdown (plan + packing slip)]
 *   [CODE]  [Status]  [Kind chip if child]   Patient · Doctor · Clinic
 *   [Action row: Start / Log / Ship / Reprint slip]
 *   [Manufacturing Spec card (read-only on this page)] [Execution Log card]
 *   [STL files panel — download buttons + "Download all" stub]
 *   [Materials Consumed (only after complete)]
 *   [Timeline]
 *
 * RBAC: page is mounted under `<ProductionRoute>` in App.js so only
 * production / coe_lead / admin can land here.
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CaseStatusBadge } from "@/components/shared/CaseStatusBadge";
import { CaseInfoChipRow } from "@/components/shared/CaseInfoChipRow";

import {
    ArrowLeft, Truck, Cube, DownloadSimple,
    Package, CheckCircle, ArrowsCounterClockwise, Crown,
    Stethoscope, Storefront, User, FileArrowUp, CaretDown, Warning,
    Barcode, ClockClockwise, Tooth, Stack, ShieldCheck,
    ArrowDown,
} from "@phosphor-icons/react";
import { ManufacturingSpecPanel } from "@/components/case/ManufacturingSpecPanel";
import CaseTimelineTab from "@/components/case/CaseTimelineTab";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ProductionWorkflowStepper, { PriorityChip } from "@/components/case/ProductionWorkflowStepper";
import MaterialsUsedPanel from "@/components/case/MaterialsUsedPanel";
import CaseNotesTab from "@/components/case/CaseNotesTab";
import PreProductionGateModal from "@/components/case/PreProductionGateModal";
import CaseSubmittedDialog from "@/components/case/CaseSubmittedDialog";
import { ProductionNextStepBanner } from "@/components/case/ProductionNextStepBanner";

const STATUS_PILLS = {
    submitted: { label: "Submitted", cls: "bg-zinc-100 text-zinc-700" },
    in_planning: { label: "In planning", cls: "bg-indigo-100 text-indigo-700" },
    sent_for_approval: { label: "Sent for approval", cls: "bg-blue-100 text-blue-700" },
    approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-700" },
    assigned_to_production: { label: "Assigned to production", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
    submitted_to_production: { label: "Doctor-self-planned", cls: "bg-cyan-100 text-cyan-700" },
    in_production: { label: "In production", cls: "bg-blue-100 text-blue-700" },
    shipped: { label: "Shipped", cls: "bg-orange-100 text-orange-700" },
    in_treatment: { label: "In treatment", cls: "bg-violet-100 text-violet-800" },
    completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-800" },
};


export default function ProductionCaseDetail() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const [c, setCase] = useState(null);
    const [wf, setWf] = useState(null);
    const [loading, setLoading] = useState(true);
    const [shipOpen, setShipOpen] = useState(false);
    /* iter98.99i — Premium "Case shipped" confirmation dialog. Fires
     * after the ship form dialog successfully posts. Captures the
     * shipped caseObj snapshot so the dialog hero shows the right code
     * even after `load()` refreshes the parent case. */
    const [shippedDialog, setShippedDialog] = useState({ open: false, code: "" });
    const [tab, setTab] = useState("workflow");
    /* iter97.24 — Pre-production gate modal owns the entire pre-flight
       flow: spec review + material pickup log. Replaces the prior direct
       call to the stepper's openAckDialog(). */
    const [gateOpen, setGateOpen] = useState(false);
    /* iter97.23 — stepper ref kept for future programmatic access. */
    const stepperRef = useRef(null);

    const load = () => {
        Promise.all([
            api.get(`/cases/${caseId}`),
            api.get(`/production/cases/${caseId}/workflow`).catch(() => ({ data: null })),
        ]).then(([cr, wr]) => {
            setCase(cr.data);
            setWf(wr.data);
        }).catch((e) => {
            toast.error(e.response?.data?.detail || "Failed to load case");
            navigate("/coe/production/queue");
        }).finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [caseId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return <div className="text-muted-foreground p-6">Loading case…</div>;
    if (!c) return null;

    const status = STATUS_PILLS[c.status] || { label: c.status, cls: "bg-zinc-100 text-zinc-700" };
    const isChild = !!c.parent_case_id;
    const kind = c.case_kind || "primary";
    const token = localStorage.getItem("ca_token") || "";
    const stlFiles = (c.files || []).filter((f) =>
        (f.filename || f.name || "").toLowerCase().match(/\.(stl|obj|ply)$/)
    );

    /* iter97.19 / iter97.22 — Ship is only safe once the case has reached
       Packaging. The legacy mfg.execution.completed_at gate has been
       removed alongside the Overview / Log-progress legacy surfaces. */
    const productionStage = c.production_stage || wf?.current_stage || null;
    const canShip = productionStage === "packaging" || productionStage === "closed";
    const shipDisabledReason = !canShip
        ? (productionStage
            ? `Complete the workflow up to Packaging before shipping (currently at ${productionStage.replace(/_/g, " ")}).`
            : "Complete manufacturing before shipping")
        : "";

    /* iter97.19 — Workflow progress bar derived from the stepper data.
       Counts how many distinct stages have been passed/skipped vs total. */
    const stages = wf?.stages || [];
    const passedSet = new Set();
    for (const h of (wf?.history || [])) {
        if (h.from_stage && h.to_stage && h.from_stage !== h.to_stage && h.status !== "pending") {
            passedSet.add(h.from_stage);
        }
    }
    const totalStages = stages.length || 10;
    const stagesPassed = passedSet.size;
    const progressPct = totalStages > 0 ? Math.min(100, Math.round((stagesPassed / totalStages) * 100)) : 0;
    const currentStageLabel = (stages.find((s) => s.slug === productionStage)?.label) || (productionStage || "—");

    return (
        <div className="max-w-6xl mx-auto" data-testid="production-case-detail">
            {/* Top bar — back + downloads */}
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <Link
                    to="/coe/production/queue"
                    className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 font-medium"
                    data-testid="production-detail-back"
                >
                    <ArrowLeft size={13} weight="bold" /> Back to queue
                </Link>
                <ProductionPdfMenu caseId={c.id} token={token} />
            </div>

            {/* Hero — compact 2-row design matching Doctor / Planner /
                QA. Row 1: avatar · name + demographic · (right) doctor
                + clinic side-by-side. Row 2: status badge · case code ·
                priority · kind chip (refinement / retainer) · batch ·
                production stage · diagnosis. Aligner snapshot row stays
                below (production-specific). */}
            <div
                className="rounded-2xl border border-border bg-card px-4 py-3 sm:px-5 sm:py-4 mb-4"
                data-testid="production-case-hero"
            >
                {/* Row 1 — identity */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    {c.patient_photo_data_url ? (
                        <img
                            src={c.patient_photo_data_url}
                            alt={c.patient_name}
                            className="w-9 h-9 rounded-full object-cover shrink-0 border border-border bg-secondary"
                            data-testid="production-patient-avatar-img"
                            loading="lazy"
                        />
                    ) : (
                        <div
                            className="w-9 h-9 rounded-full shrink-0 bg-primary/10 text-primary font-semibold grid place-items-center text-sm"
                            data-testid="production-patient-avatar"
                            aria-label={`Initials for ${c.patient_name || "patient"}`}
                        >
                            {(c.patient_name || "?").split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase() || "?"}
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h1
                            className="font-display text-base sm:text-lg font-bold leading-tight truncate"
                            data-testid="production-detail-patient"
                        >
                            {c.patient_name || "Patient"}
                        </h1>
                        <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                            {[
                                c.patient_age && `${c.patient_age} years`,
                                c.patient_gender && (c.patient_gender.charAt(0).toUpperCase() + c.patient_gender.slice(1)),
                                c.patient_code && `ID: ${c.patient_code}`,
                            ].filter(Boolean).join(", ") || "—"}
                            {c.diagnosis && !/^TEST[_\s]/i.test(c.diagnosis.trim()) && (
                                <span className="text-muted-foreground/60 truncate">· {c.diagnosis}</span>
                            )}
                        </div>
                    </div>
                    {/* Doctor + clinic top-right side-by-side */}
                    <div className="flex items-center gap-2.5 text-[11px] sm:text-xs shrink-0 flex-wrap" data-testid="production-detail-hero-right">
                        <div
                            className="inline-flex items-center gap-1 text-muted-foreground truncate max-w-[200px]"
                            title={`Provider: Dr. ${(c.doctor_name || "").replace(/^Dr\.\s*/, "")}`}
                        >
                            <Stethoscope size={12} weight="bold" />
                            <span className="truncate font-medium">
                                Dr. {(c.doctor_name || "").replace(/^Dr\.\s*/, "")}
                            </span>
                        </div>
                        {(c.clinic_name || c.practice_location_name) && (
                            <>
                                <div className="hidden sm:block h-3 w-px bg-border" />
                                <div
                                    className="inline-flex items-center gap-1 text-muted-foreground truncate max-w-[220px]"
                                    title={c.clinic_name || c.practice_location_name}
                                >
                                    <Storefront size={12} weight="bold" />
                                    <span className="truncate">{c.clinic_name || c.practice_location_name}</span>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* iter98.99de — Status pill promoted out of the meta row
                    into its OWN row above (size="lg" capsule) per user
                    direction (universal across all portals). */}
                <div className="mt-2.5 flex items-center">
                    <span data-testid="case-status-pill">
                        <CaseStatusBadge status={c.status} size="lg" productionStage={c.production_stage} />
                    </span>
                </div>

                {/* iter100.75 — Universal informational chip row. Rendered
                    ABOVE the production-specific chip row so the tech sees
                    the same "Case ID / Created / Planning / Manufacturing /
                    IO Scans / Compliance" band that every other portal
                    shows, then production-specific chips (priority, kind,
                    batch, stage) directly below. */}
                <div className="mt-2">
                    <CaseInfoChipRow caseObj={c} viewerRole="production" />
                </div>

                {/* Row 2 — meta row: status, code, priority, kind chip,
                    batch, stage. Production-specific chips stay clustered
                    here so the tech sees workflow state at a glance. */}
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[11px]">
                    <span className="font-mono font-semibold tabular-nums text-foreground/80" data-testid="production-detail-code">
                        {c.code || c.id?.slice(0, 8) || "—"}
                    </span>
                    <PriorityChip priority={c.production_priority} size="sm" testId="production-detail-priority" />
                    {kind === "refinement" && (
                        <Link
                            to={c.parent_case_id ? `/coe/production/case/${c.parent_case_id}` : "#"}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200"
                            title={c.parent_case_code ? `Refinement of ${c.parent_case_code}` : "Refinement"}
                            data-testid="production-detail-kind-chip"
                        >
                            <ArrowsCounterClockwise size={11} weight="fill" /> Refinement
                            {c.parent_case_code && <span className="font-mono ml-1 normal-case">· {c.parent_case_code}</span>}
                        </Link>
                    )}
                    {kind === "retainer" && (
                        <Link
                            to={c.parent_case_id ? `/coe/production/case/${c.parent_case_id}` : "#"}
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-violet-100 text-violet-800 border border-violet-200 hover:bg-violet-200"
                            title={c.parent_case_code ? `Retainer for ${c.parent_case_code}` : "Retainer"}
                            data-testid="production-detail-kind-chip"
                        >
                            <Crown size={11} weight="fill" /> Retainer
                            {c.parent_case_code && <span className="font-mono ml-1 normal-case">· {c.parent_case_code}</span>}
                        </Link>
                    )}
                    {c.batch_id && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-secondary text-foreground border border-border" title="Batch ID">
                            <Barcode size={10} weight="bold" /> {c.batch_id}
                        </span>
                    )}
                    {/* iter97.19 — Current production stage chip pulled
                        inline. Saves the tech a click into the workflow tab. */}
                    {productionStage && (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20"
                            title="Current production stage"
                            data-testid="production-detail-stage-chip"
                        >
                            <ShieldCheck size={11} weight="fill" /> {currentStageLabel}
                        </span>
                    )}
                </div>
                {/* iter97.19 — Manufacturing snapshot row: aligner counts
                    + material spec. These are what the tech actually
                    fabricates against — surfacing them on the hero
                    saves a click into the spec tab. */}
                {(c.plan?.aligner_count_upper != null || c.plan?.aligner_count_lower != null || c.manufacturing_spec?.sheet_name) && (
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {(c.plan?.aligner_count_upper != null || c.plan?.aligner_count_lower != null) && (
                            <span
                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-primary/10 text-primary border border-primary/20 font-bold tabular-nums"
                                title="Upper / Lower aligner count"
                                data-testid="production-detail-aligners-chip"
                            >
                                <Tooth size={11} weight="fill" />
                                U{c.plan?.aligner_count_upper ?? 0} · L{c.plan?.aligner_count_lower ?? 0}
                            </span>
                        )}
                        {c.manufacturing_spec?.sheet_name && (
                            <span
                                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-cyan-50 text-cyan-800 border border-cyan-200 font-bold truncate max-w-[300px]"
                                title={c.manufacturing_spec.sheet_name}
                                data-testid="production-detail-spec-chip"
                            >
                                <Stack size={11} weight="fill" /> {c.manufacturing_spec.sheet_name}
                            </span>
                        )}
                    </div>
                )}

                {/* Action buttons — gated by status. iter97.23:
                    • "Add note" removed from header (lives in the new Notes tab).
                    • "Start manufacturing" replaced by context-aware
                      "Acknowledge & begin" that triggers the existing
                      AcknowledgeDialog via stepperRef + switches the tab.
                    iter98.99bw — moved out of right-column into a full
                    row below the aligner snapshot so the hero stays
                    compact and the buttons stay readable. */}
                <div className="flex items-center gap-2 flex-wrap mt-3" data-testid="production-detail-actions">
                        {/* Acknowledge & begin — only visible pre-acknowledgement.
                            iter97.24: opens the Pre-Production Gate modal
                            which combines spec review + material pickup.
                            iter97.29: blocked when no STL/OBJ/PLY files are
                            attached — production cannot start without them. */}
                        {!wf?.acknowledged_at
                          && (c.status === "assigned_to_production" || c.status === "submitted_to_production" || c.status === "approved" || c.status === "in_production") && (
                            <div className="flex flex-col items-end gap-1.5">
                                <Button
                                    onClick={() => setGateOpen(true)}
                                    className="rounded-full"
                                    disabled={stlFiles.length === 0}
                                    title={stlFiles.length === 0 ? "STL files missing — cannot begin production. Contact admin." : ""}
                                    data-testid="production-detail-acknowledge"
                                >
                                    <CheckCircle size={14} className="mr-1.5" weight="fill" /> Acknowledge & begin
                                </Button>
                                {stlFiles.length === 0 && (
                                    <div
                                        className="text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1 max-w-[280px] leading-tight"
                                        data-testid="production-detail-stl-missing-warning"
                                    >
                                        <Warning size={11} weight="fill" className="inline-block mr-1 -mt-0.5" />
                                        STL files missing — cannot begin production. Contact admin.
                                    </div>
                                )}
                            </div>
                        )}
                        {/* Post-ack convenience: jump to the active step. Hidden
                            when workflow is closed or shipped. */}
                        {wf?.acknowledged_at && c.status === "in_production" && productionStage && productionStage !== "closed" && (
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setTab("workflow");
                                    setTimeout(() => {
                                        const el = document.querySelector(`[data-testid="workflow-stage-${productionStage}"]`);
                                        el?.scrollIntoView({ behavior: "smooth", block: "center" });
                                    }, 100);
                                }}
                                className="rounded-full"
                                data-testid="production-detail-view-active"
                            >
                                <ArrowDown size={14} className="mr-1.5" weight="bold" /> View active step
                            </Button>
                        )}
                        {c.status === "in_production" && (
                            <Button
                                onClick={() => setShipOpen(true)}
                                className="rounded-full bg-orange-600 hover:bg-orange-700 text-white"
                                disabled={!canShip}
                                title={shipDisabledReason}
                                data-testid="production-detail-ship"
                            >
                                <Truck size={14} className="mr-1.5" /> Ship
                            </Button>
                        )}
                        {c.status === "shipped" && (
                            <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 font-semibold">
                                <CheckCircle size={14} weight="fill" /> Out for delivery
                            </span>
                        )}
                    </div>

                {/* iter97.19 — Workflow progress bar. Shows X of N stages
                    completed. Only renders for active production states
                    (not for shipped/completed). */}
                {wf && stages.length > 0 && c.status !== "shipped" && c.status !== "completed" && (
                    <div className="mt-4 pt-4 border-t border-border" data-testid="production-detail-progress">
                        <div className="flex items-center justify-between mb-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
                            <span>Production progress</span>
                            <span className="tabular-nums">{stagesPassed} / {totalStages} stages</span>
                        </div>
                        <div className="h-2 bg-secondary/60 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-gradient-to-r from-primary to-emerald-500 transition-all duration-500"
                                style={{ width: `${progressPct}%` }}
                                data-testid="production-detail-progress-bar"
                            />
                        </div>
                        <div className="flex items-center justify-between mt-1.5 text-[10px] text-muted-foreground">
                            <span>{progressPct}% complete</span>
                            {productionStage && <span>Current: <span className="font-semibold text-foreground">{currentStageLabel}</span></span>}
                        </div>
                    </div>
                )}
            </div>

            {/* iter101.13 — Continuous Flow Audit. The tech's "next step"
                signage now covers 4 handoffs (acknowledge → QC review →
                packaging → ship) instead of just the acknowledge gate
                from iter101.8. Component self-hides during mid-flow work
                (thermoforming, trimming, etc.) so the tech isn't nagged
                while they're actually building the aligners. */}
            <ProductionNextStepBanner
                c={c}
                wf={wf}
                stlFiles={stlFiles}
                productionStage={productionStage}
                canShip={canShip}
                shipDisabledReason={shipDisabledReason}
                onAcknowledge={() => setGateOpen(true)}
                onNavigateToStage={(dest, stageKey) => {
                    if (dest === "stl") {
                        setTab("stl");
                        return;
                    }
                    setTab("workflow");
                    if (stageKey) {
                        setTimeout(() => {
                            const el = document.querySelector(`[data-testid="workflow-stage-${stageKey}"]`);
                            el?.scrollIntoView({ behavior: "smooth", block: "center" });
                        }, 100);
                    }
                }}
                onOpenShipDialog={() => setShipOpen(true)}
            />

            {/* Tabs — same Shadcn pattern as PlannerCaseDetail / Doctor's CaseDetail
                so the production tech's mental model maps cleanly across the
                COE portal. Hero (above) acts as the page anchor; tabs scope
                everything below to one concern at a time. */}
            <Tabs value={tab} onValueChange={setTab} className="space-y-4" data-testid="production-detail-tabs">
                <TabsList className="bg-secondary rounded-full p-1 h-auto flex-wrap">
                    <TabsTrigger value="workflow" className="rounded-full px-4 py-1.5 text-xs" data-testid="production-tab-workflow">
                        Workflow
                    </TabsTrigger>
                    <TabsTrigger value="spec" className="rounded-full px-4 py-1.5 text-xs" data-testid="production-tab-spec">
                        Manufacturing spec
                    </TabsTrigger>
                    <TabsTrigger value="stl" className="rounded-full px-4 py-1.5 text-xs" data-testid="production-tab-stl">
                        STL files
                        {stlFiles.length > 0 && <span className="ml-1.5 text-[9px] font-normal text-muted-foreground">· {stlFiles.length}</span>}
                    </TabsTrigger>
                    <TabsTrigger value="materials" className="rounded-full px-4 py-1.5 text-xs" data-testid="production-tab-materials">
                        Materials
                        {Array.isArray(c.materials_used) && c.materials_used.length > 0 && (
                            <span className="ml-1.5 text-[9px] font-normal text-muted-foreground">· {c.materials_used.length}</span>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="notes" className="rounded-full px-4 py-1.5 text-xs" data-testid="production-tab-notes">
                        Notes
                    </TabsTrigger>
                    <TabsTrigger value="timeline" className="rounded-full px-4 py-1.5 text-xs" data-testid="production-tab-timeline">
                        Timeline
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="workflow" className="space-y-4">
                    {/* iter97.29 — Tech-panel sidebar removed (per user
                        request). Step 1 info card + the case header now
                        carry all the context the tech needs. Full-width
                        stepper. */}
                    <ProductionWorkflowStepper
                        ref={stepperRef}
                        caseId={c.id}
                        caseObj={c}
                        onChange={load}
                    />
                </TabsContent>

                <TabsContent value="spec">
                    <ManufacturingSpecPanel caseId={c.id} status={c.status} plan={c.plan} allowEdit={false} />
                </TabsContent>

                <TabsContent value="stl">
                    <StlFilesPanel files={stlFiles} token={token} />
                </TabsContent>

                <TabsContent value="materials" className="space-y-4">
                    {/* iter97.18 — Active Materials Used log entry surface.
                        Pre-fills Expected BOM (auto-derived from plan +
                        manufacturing_spec), captures Actual + Wastage +
                        Reason. Blocks QC sign-off until filled.
                        After submission, decrements stock + writes a
                        Production Usage stock_movement. */}
                    <MaterialsUsedPanel
                        caseObj={c}
                        canEdit={c.status === "in_production" || c.status === "assigned_to_production" || c.status === "submitted_to_production"}
                        onChange={load}
                    />
                    {/* Historical batch/lot traceability — once mfg is
                        complete, the legacy MaterialsConsumed panel shows
                        which specific lots fed this case. */}
                    {Array.isArray(c.materials_used) && c.materials_used.length > 0 && (
                        <MaterialsConsumed materials={c.materials_used} />
                    )}
                </TabsContent>

                <TabsContent value="notes" className="space-y-4">
                    {/* iter97.23 — Unified Notes tab. Surfaces admin
                        assignment notes, production tech notes, and
                        actionable timeline events with prose. The single
                        home for Add-note CTA (replaces header + tech-panel
                        buttons). */}
                    <CaseNotesTab caseObj={c} onChange={load} />
                </TabsContent>

                <TabsContent value="timeline">
                    <div className="rounded-2xl border border-border bg-card overflow-hidden">
                        <div className="px-5 py-3 border-b border-border bg-secondary/20 flex items-center gap-2">
                            <ClockClockwise size={14} weight="duotone" className="text-primary" />
                            <h2 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Audit timeline</h2>
                        </div>
                        <div className="p-5">
                            <CaseTimelineTab caseObj={c} />
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Dialogs (iter97.23 — StartManufacturingDialog removed; ack
                handled inside the stepper via ref). */}
            <ShipFromDetailDialog
                caseObj={shipOpen ? c : null}
                onClose={() => setShipOpen(false)}
                onShipped={() => {
                    /* iter98.99i — Snapshot the case code before reload()
                     * potentially refreshes the parent object, then fire
                     * the premium "Case shipped" CaseSubmittedDialog. */
                    setShippedDialog({ open: true, code: c?.code || "" });
                    setShipOpen(false);
                    load();
                }}
            />

            {/* iter97.24 — Pre-Production Gate modal */}
            <PreProductionGateModal
                open={gateOpen}
                caseObj={c}
                onClose={() => setGateOpen(false)}
                onConfirmed={() => { setGateOpen(false); load(); }}
            />

            {/* iter98.99i — Premium "Case shipped · on its way to the
                clinic" confirmation. Primary CTA returns to the queue. */}
            <CaseSubmittedDialog
                open={shippedDialog.open}
                flow="case_shipped"
                caseCode={shippedDialog.code}
                primaryLabel="Back to queue"
                onPrimary={() => {
                    setShippedDialog({ open: false, code: "" });
                    navigate("/coe/production");
                }}
            />
        </div>
    );
}


/* ─── iter97.19 — Production tech context panel ──────────────────────────────
   Sticky right-rail surface on the Workflow tab. Surfaces the info a tech
   actually needs while fabricating — current stage, time in stage, progress
   counters, quick links to materials + STL + note. */
/* iter97.23 — Slimmed Tech Panel.
 *
 * Per user spec #2/#3/#4/#7: the panel must ONLY surface info that's not
 * already visible elsewhere on the page. That means we drop:
 *   • Current stage box  (already in the header badge + stage chip)
 *   • Progress 0/N box   (already in the production progress bar)
 *   • Add note button    (lives on the new Notes tab)
 *
 * And keep:
 *   • Manufacturing spec quick reference (SKU + aligner counts + retainer)
 *   • Assigned tech name + shift
 *   • Last 3 timeline events in a compact, scannable list
 */
function ProductionTechPanel({ caseObj, wf, timeline = [] }) {
    const c = caseObj;
    const ackBy = wf?.acknowledged_by_name;
    const fab = wf?.fabricator_user_name;
    const shift = wf?.shift || wf?.acknowledged_shift;
    const recentEvents = (timeline || [])
        .slice()
        .reverse()
        .slice(0, 3);

    return (
        <aside
            className="lg:sticky lg:top-4 rounded-2xl border border-border bg-card p-4 space-y-3"
            data-testid="production-tech-panel"
        >
            <div>
                <div className="text-[9px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Tech panel</div>
                <div className="text-xs text-muted-foreground mt-0.5">Quick reference — info not shown elsewhere on the page.</div>
            </div>

            {/* Manufacturing spec quick reference (always present if data exists) */}
            {(c.plan?.aligner_count_upper != null
              || c.plan?.aligner_count_lower != null
              || c.manufacturing_spec?.sheet_name
              || c.manufacturing_spec?.retainer_sku) && (
                <div
                    className="rounded-xl border border-border p-3 space-y-1.5"
                    data-testid="production-tech-panel-spec"
                >
                    <div className="text-[9px] font-bold tracking-[0.16em] uppercase text-muted-foreground">Manufacturing spec</div>
                    {c.manufacturing_spec?.sheet_name && (
                        <div className="text-xs flex items-start gap-1.5">
                            <Stack size={11} weight="fill" className="text-cyan-700 mt-0.5 shrink-0" />
                            <span className="font-semibold text-cyan-900 truncate" title={c.manufacturing_spec.sheet_name}>
                                {c.manufacturing_spec.sheet_name}
                            </span>
                        </div>
                    )}
                    {(c.plan?.aligner_count_upper != null || c.plan?.aligner_count_lower != null) && (
                        <div className="text-xs flex items-center gap-1.5">
                            <Tooth size={11} weight="fill" className="text-primary" />
                            <span className="font-semibold tabular-nums">U{c.plan?.aligner_count_upper ?? 0} · L{c.plan?.aligner_count_lower ?? 0}</span>
                            <span className="text-muted-foreground text-[10px]">aligners</span>
                        </div>
                    )}
                    {c.manufacturing_spec?.retainer_sku && (
                        <div className="text-xs flex items-center gap-1.5">
                            <Crown size={11} weight="fill" className="text-violet-600" />
                            <span className="font-mono font-semibold text-foreground">{c.manufacturing_spec.retainer_sku}</span>
                            <span className="text-muted-foreground text-[10px]">retainer</span>
                        </div>
                    )}
                </div>
            )}

            {/* Assigned tech + shift */}
            {(ackBy || fab) && (
                <div
                    className="rounded-xl border border-border p-3 space-y-1"
                    data-testid="production-tech-panel-team"
                >
                    <div className="text-[9px] font-bold tracking-[0.16em] uppercase text-muted-foreground flex items-center justify-between">
                        <span>Assigned</span>
                        {shift && (
                            <span className="font-normal normal-case tracking-normal text-[10px] text-foreground/70">
                                {String(shift).charAt(0).toUpperCase() + String(shift).slice(1)} shift
                            </span>
                        )}
                    </div>
                    {fab && (
                        <div className="text-xs flex items-center gap-1.5">
                            <User size={11} weight="fill" className="text-primary" />
                            <span className="font-semibold truncate">{fab}</span>
                        </div>
                    )}
                    {ackBy && !fab && (
                        <div className="text-xs flex items-center gap-1.5">
                            <CheckCircle size={11} weight="fill" className="text-emerald-600" />
                            <span className="font-semibold truncate">{ackBy}</span>
                            <span className="text-[10px] text-muted-foreground">(ack)</span>
                        </div>
                    )}
                </div>
            )}

            {/* Last 3 timeline events */}
            {recentEvents.length > 0 && (
                <div
                    className="rounded-xl border border-border p-3 space-y-2"
                    data-testid="production-tech-panel-timeline"
                >
                    <div className="text-[9px] font-bold tracking-[0.16em] uppercase text-muted-foreground">Last 3 events</div>
                    <ol className="space-y-1.5">
                        {recentEvents.map((e, i) => (
                            <li key={e.id || i} className="text-[11px] leading-tight">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-semibold text-foreground truncate">
                                        {humaniseTimelineEvent(e)}
                                    </span>
                                    <span className="text-[9px] text-muted-foreground tabular-nums shrink-0">
                                        {formatTimeShort(e.at || e.created_at)}
                                    </span>
                                </div>
                                {e.by_name && (
                                    <div className="text-[10px] text-muted-foreground truncate">{e.by_name}</div>
                                )}
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </aside>
    );
}


function humaniseTimelineEvent(e) {
    const ev = e.event || e.type || "Activity";
    const map = {
        assigned_to_production:               "Routed to production",
        production_acknowledged:              "Tech acknowledged",
        production_stage_advanced:            "Stage advanced",
        production_on_hold:                   "Case held",
        production_resumed:                   "Hold resumed",
        production_escalation_raised:         "Escalation raised",
        production_escalation_acknowledged:   "Escalation cleared",
        production_rework_initiated:          "Rework initiated",
        production_materials_logged:          "Materials logged",
        production_materials_log_unlocked:    "Materials log unlocked",
        case_shipped:                         "Case shipped",
        case_unlocked:                        "Case unlocked",
    };
    return map[ev] || ev.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}


function formatTimeShort(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return "now";
    if (diffMin < 60) return `${diffMin}m`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h`;
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}


/* ─── Production PDF menu — re-uses the same dropdown pattern as CasePdfMenu
   but offers only the docs the production tech actually needs while
   working a case: the Packing Slip. The Treatment Plan was previously
   here but removed in iter97.22 — the tech manufactures against the
   Manufacturing Spec tab + STL files, not the patient-facing plan.
   iter101.58 — Switched from native `<details>` to a controlled dropdown
   so clicking outside closes it (matches CasePdfMenu behaviour). */
function ProductionPdfMenu({ caseId, token }) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handleClick = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        const handleKey = (e) => { if (e.key === "Escape") setOpen(false); };
        document.addEventListener("mousedown", handleClick);
        document.addEventListener("touchstart", handleClick);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleClick);
            document.removeEventListener("touchstart", handleClick);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open]);

    if (!caseId) return null;
    const items = [
        { key: "packing-slip", title: "Packing Slip", sub: "Print + drop in the carton" },
    ];
    return (
        <div ref={rootRef} className="relative inline-block" data-testid="production-detail-pdf-menu">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="list-none inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-primary border border-primary/30 hover:bg-primary/5 transition-colors cursor-pointer select-none"
                aria-expanded={open}
                aria-haspopup="menu"
                data-testid="production-detail-pdf-toggle"
            >
                <FileArrowUp size={12} weight="bold" /> Download PDF
                <CaretDown
                    size={10}
                    weight="bold"
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <div
                    role="menu"
                    className="absolute right-0 top-full mt-1 z-30 min-w-[220px] rounded-xl border border-border bg-card shadow-lg overflow-hidden"
                >
                    {items.map((it, idx) => (
                        <a
                            key={it.key}
                            href={`${API}/cases/${caseId}/pdf/${it.key}?auth=${token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            role="menuitem"
                            onClick={() => setOpen(false)}
                            className={`block px-4 py-2.5 text-xs hover:bg-secondary ${idx > 0 ? "border-t border-border" : ""}`}
                            data-testid={`production-detail-pdf-${it.key}`}
                        >
                            <div className="font-semibold text-foreground">{it.title}</div>
                            <div className="text-[10px] text-muted-foreground">{it.sub}</div>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
}


/* ─── STL files panel — the tech needs to print these 3D models. */
function StlFilesPanel({ files, token }) {
    if (!files || files.length === 0) {
        return (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mb-4 flex items-center gap-3" data-testid="production-stl-empty">
                <Warning size={16} weight="fill" className="text-amber-700 shrink-0" />
                <div className="text-xs text-amber-900">
                    <span className="font-semibold">No STL / OBJ files uploaded yet.</span>
                    <span className="text-amber-800/80"> Ping the planner before starting — you need the 3D models to print.</span>
                </div>
            </div>
        );
    }
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden mb-4" data-testid="production-stl-panel">
            <div className="px-5 py-3 border-b border-border bg-secondary/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Cube size={14} weight="duotone" className="text-primary" />
                    <h2 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                        3D model files <span className="font-normal text-muted-foreground">· {files.length}</span>
                    </h2>
                </div>
            </div>
            <div className="divide-y divide-border">
                {files.map((f) => (
                    <div key={f.id || f.filename} className="px-5 py-3 flex items-center justify-between" data-testid={`production-stl-${f.id || f.filename}`}>
                        <div className="min-w-0">
                            <div className="font-mono text-xs text-foreground truncate">{f.filename || f.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                                {f.kind && <span className="uppercase font-semibold mr-1.5">{f.kind}</span>}
                                {f.size ? `${(f.size / (1024 * 1024)).toFixed(2)} MB` : ""}
                            </div>
                        </div>
                        <a
                            href={f.url ? `${API}${f.url}${f.url.includes("?") ? "&" : "?"}auth=${token}` : `${API}/cases/files/${f.id}?auth=${token}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary font-semibold hover:underline inline-flex items-center gap-1"
                            data-testid={`production-stl-download-${f.id || f.filename}`}
                        >
                            <DownloadSimple size={12} /> Download
                        </a>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* ─── Materials consumed — populated by /manufacturing/complete (FEFO batch
   consumption). Surfaces lot numbers + supplier + expiry for traceability. */
function MaterialsConsumed({ materials }) {
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden mb-4" data-testid="production-materials-consumed">
            <div className="px-5 py-3 border-b border-border bg-secondary/20 flex items-center gap-2">
                <Package size={14} weight="duotone" className="text-primary" />
                <h2 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Materials consumed (traceability)</h2>
            </div>
            <table className="w-full text-xs">
                <thead className="bg-secondary/10 text-muted-foreground">
                    <tr>
                        <th className="text-left px-5 py-2 font-semibold uppercase tracking-wider text-[9px]">SKU</th>
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[9px]">Name</th>
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[9px]">Batch</th>
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[9px]">Lot</th>
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[9px]">Supplier</th>
                        <th className="text-right px-5 py-2 font-semibold uppercase tracking-wider text-[9px]">Qty</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-border">
                    {materials.map((m, i) => (
                        <tr key={i} data-testid={`production-material-row-${i}`}>
                            <td className="px-5 py-2 font-mono text-foreground">{m.material_sku || "—"}</td>
                            <td className="px-3 py-2 text-foreground">{m.material_name || "—"}</td>
                            <td className="px-3 py-2 font-mono text-muted-foreground">{m.batch_code || "—"}</td>
                            <td className="px-3 py-2 font-mono text-muted-foreground">{m.lot_number || "—"}</td>
                            <td className="px-3 py-2 text-muted-foreground">{m.supplier || "—"}</td>
                            <td className="px-5 py-2 text-right font-bold text-foreground">{m.qty_consumed || 0}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}


/* ─── Ship dialog — same as ProductionQueue's but lives here too so the tech
   can ship from the detail page without round-tripping. */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function ShipFromDetailDialog({ caseObj, onClose, onShipped }) {
    const [batchId, setBatchId] = useState("");
    const [carrier, setCarrier] = useState("");
    const [trackingNumber, setTrackingNumber] = useState("");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (caseObj) {
            setBatchId(caseObj.batch_id || "");
            setCarrier(caseObj.shipping_carrier || "");
            setTrackingNumber(caseObj.tracking_number || "");
            setNote("");
        }
    }, [caseObj]);

    const submit = async () => {
        if (!caseObj) return;
        setBusy(true);
        try {
            await api.put(`/production/cases/${caseObj.id}/ship`, {
                batch_id: batchId, carrier, tracking_number: trackingNumber, note,
            });
            // iter98.99i — Replaced the thin "Case shipped" toast with the
            // premium CaseSubmittedDialog mounted on the parent. The parent
            // shows it via onShipped() below.
            onShipped();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to ship");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={!!caseObj} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="production-detail-ship-dialog">
                <DialogHeader>
                    <DialogTitle>Mark case as shipped</DialogTitle>
                </DialogHeader>
                {caseObj && (
                    <div className="space-y-3 mt-2">
                        <p className="text-sm text-muted-foreground">{caseObj.patient_name} · {caseObj.doctor_name}</p>
                        <div>
                            <Label className="text-xs font-semibold">Batch ID</Label>
                            <Input value={batchId} onChange={(e) => setBatchId(e.target.value)} placeholder="e.g. BATCH-2026-0142" className="h-10 mt-1" data-testid="ship-batch-id" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Carrier</Label>
                            <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Blue Dart, Delhivery..." className="h-10 mt-1" data-testid="ship-carrier" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Tracking number</Label>
                            <Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} className="h-10 mt-1" data-testid="ship-tracking" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold">Note (optional)</Label>
                            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="mt-1" data-testid="ship-note" />
                        </div>
                    </div>
                )}
                <DialogFooter className="mt-3">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="ship-submit">
                        {busy ? "Shipping…" : "Confirm ship"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
